// =============================================================================
// Repository: Profile — fetch, create, avatar upload/remove + profile updates
// =============================================================================

import { supabase } from '@/integrations/supabase/client';
import type { Profile } from '@/types/database';

// ─────────────────────────────────────────────────────────────────────────────
// FETCH PROFILE
// ─────────────────────────────────────────────────────────────────────────────
export async function fetchProfile(userId: string): Promise<Profile | null> {
  const { data, error } = await supabase
    .from('core_profiles')
    .select('*')
    .eq('id', userId)
    .maybeSingle();

  if (error) {
    console.error('Error fetching profile:', error);
    return null;
  }

  return data as Profile | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// ENSURE PROFILE EXISTS (create if missing)
// ─────────────────────────────────────────────────────────────────────────────
export async function ensureProfileExists(
  userId: string,
  email: string
): Promise<Profile | null> {
  const existing = await fetchProfile(userId);
  if (existing) return existing;

  console.log('Profile not found, creating one through backend for user:', userId);
  const { data: authData } = await supabase.auth.getUser();
  const meta = authData.user?.user_metadata as Record<string, unknown> | undefined;
  const fullName =
    (typeof meta?.full_name === 'string' && meta.full_name.trim()) ||
    (typeof meta?.name === 'string' && meta.name.trim()) ||
    [meta?.first_name, meta?.last_name].filter(Boolean).join(' ').trim() ||
    email.split('@')[0];

  const { data, error } = await supabase.functions.invoke('ensure-profile', {
    body: { full_name: fullName },
  });

  if (error || data?.error) {
    console.error('Error creating profile:', error || data?.error);
    return null;
  }

  return data.profile as Profile;
}

// ─────────────────────────────────────────────────────────────────────────────
// UPDATE PROFILE
// ─────────────────────────────────────────────────────────────────────────────
export async function updateProfile(
  userId: string,
  data: { full_name?: string | null; phone?: string | null; job_title?: string | null },
) {
  const { error } = await supabase
    .from('core_profiles')
    .update(data)
    .eq('id', userId);
  if (error) throw error;
}

// ─────────────────────────────────────────────────────────────────────────────
// AVATAR
// ─────────────────────────────────────────────────────────────────────────────
export async function uploadAvatar(userId: string, file: File): Promise<string> {
  const fileExt = file.name.split('.').pop();
  const filePath = `avatars/${userId}-${Date.now()}.${fileExt}`;

  const { error: uploadError } = await supabase.storage
    .from('avatars')
    .upload(filePath, file, { upsert: true });
  if (uploadError) throw uploadError;

  const {
    data: { publicUrl },
  } = supabase.storage.from('avatars').getPublicUrl(filePath);

  const { error: updateError } = await supabase
    .from('core_profiles')
    .update({ avatar_url: publicUrl })
    .eq('id', userId);
  if (updateError) throw updateError;

  return publicUrl;
}

export async function removeAvatar(userId: string) {
  const { error } = await supabase
    .from('core_profiles')
    .update({ avatar_url: null })
    .eq('id', userId);
  if (error) throw error;
}

// ─────────────────────────────────────────────────────────────────────────────
// LAST ORG ID (cross-device persistence of selected workspace)
// ─────────────────────────────────────────────────────────────────────────────
export async function fetchLastOrgId(userId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('core_profiles')
    .select('last_org_id')
    .eq('id', userId)
    .maybeSingle();
  if (error) {
    console.warn('[profileRepository] fetchLastOrgId failed', error);
    return null;
  }
  return ((data as any)?.last_org_id as string | null | undefined) ?? null;
}

export function updateLastOrgId(userId: string, orgId: string): void {
  // Fire-and-forget: persistance non bloquante de l'org actif.
  void supabase
    .from('core_profiles')
    .update({ last_org_id: orgId })
    .eq('id', userId);
}
